import type { ProviderVersionFinding } from '@/lib/apiTypes'

export type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low'
export type FixableFilter = 'all' | 'fixable' | 'not_fixable'

export const SEVERITY_FILTERS: SeverityFilter[] = ['all', 'critical', 'high', 'medium', 'low']
export const FIXABLE_FILTERS: FixableFilter[] = ['all', 'fixable', 'not_fixable']
/** Segment colours. 'all' is deliberately absent everywhere: it stays on the neutral accent. */
export const SEVERITY_FILTER_COLOR: Partial<Record<SeverityFilter, string>> = {
  critical: 'var(--red)', high: 'var(--red)', medium: 'var(--amber)', low: 'var(--blue)',
}
export const FIXABLE_FILTER_COLOR: Partial<Record<FixableFilter, string>> = {
  fixable: 'var(--blue)', not_fixable: 'var(--red)',
}

export const matchesSeverity = (f: ProviderVersionFinding, s: SeverityFilter): boolean =>
  s === 'all' || f.severity === s
export const matchesFixable = (f: ProviderVersionFinding, x: FixableFilter): boolean =>
  x === 'all' || (x === 'fixable' ? f.fixable : !f.fixable)

/**
 * Per-bucket counts for every filter segment, CROSS-FILTERED: each group is counted over the
 * findings the OTHER groups already admit, which is the standard faceted-filter contract - every
 * number is exactly what clicking that segment would show.
 *
 * The counts used to be taken over the whole unfiltered list, so "Critical (0)" sat next to
 * "Fixable (5)" and clicking either could land on an empty table while the segments still
 * advertised findings. Now a bucket that holds nothing reads 0 and SegmentedFilter disables it, so
 * the empty state is unreachable by clicking.
 */
export function filterCounts(findings: ProviderVersionFinding[], severity: SeverityFilter, fixable: FixableFilter): {
  severity: Record<SeverityFilter, number>
  fixable: Record<FixableFilter, number>
} {
  const sev: Record<SeverityFilter, number> = { all: 0, critical: 0, high: 0, medium: 0, low: 0 }
  const fix: Record<FixableFilter, number> = { all: 0, fixable: 0, not_fixable: 0 }
  for (const f of findings) {
    // Severity buckets are counted under the CURRENT fixable choice, and vice versa. A severity
    // the segments don't offer (e.g. 'unknown') still counts toward 'all' - 'all' applies no
    // severity filter, so it must show those rows too.
    if (matchesFixable(f, fixable)) {
      sev.all += 1
      if (f.severity in sev && f.severity !== 'all') sev[f.severity as Exclude<SeverityFilter, 'all'>] += 1
    }
    if (matchesSeverity(f, severity)) {
      fix.all += 1
      fix[f.fixable ? 'fixable' : 'not_fixable'] += 1
    }
  }
  return { severity: sev, fixable: fix }
}
